import React, { useState, useMemo } from 'react';
import { EstateAssets, EstateProfile } from '../../types';
import { SectionHeader } from '../ui';
import {
    generateEvidenceRequests,
    generateEvidenceEmailText,
    EvidenceCategory,
    EvidenceRequest,
} from '../../server/services/evidenceGenerator';
import {
    FileText, ChevronDown, ChevronUp, Mail, Copy, Check,
    Home, Landmark, TrendingUp, Heart, Gift, CreditCard,
    Shield, Umbrella, Globe, ClipboardList,
} from 'lucide-react';

interface Props {
    assets: EstateAssets;
    profile: EstateProfile;
}

const CATEGORY_META: Record<EvidenceCategory, { label: string; icon: React.ReactNode }> = {
    property: { label: 'Property', icon: <Home className="w-4 h-4" /> },
    bank_account: { label: 'Bank Accounts', icon: <Landmark className="w-4 h-4" /> },
    investment: { label: 'Investments', icon: <TrendingUp className="w-4 h-4" /> },
    pension: { label: 'Pensions', icon: <Heart className="w-4 h-4" /> },
    lifetime_gift: { label: 'Lifetime Gifts', icon: <Gift className="w-4 h-4" /> },
    debt: { label: 'Debts & Liabilities', icon: <CreditCard className="w-4 h-4" /> },
    ownership: { label: 'Ownership Verification', icon: <ClipboardList className="w-4 h-4" /> },
    relief: { label: 'Tax Reliefs & Allowances', icon: <Shield className="w-4 h-4" /> },
    life_insurance: { label: 'Life Insurance', icon: <Umbrella className="w-4 h-4" /> },
    foreign_asset: { label: 'Foreign Assets', icon: <Globe className="w-4 h-4" /> },
    general: { label: 'General', icon: <FileText className="w-4 h-4" /> },
};

export const EvidenceRequestPanel: React.FC<Props> = ({ assets, profile }) => {
    const result = useMemo(() => generateEvidenceRequests(assets, profile), [assets, profile]);
    const [expandedCats, setExpandedCats] = useState<Set<EvidenceCategory>>(new Set());
    const [showEmail, setShowEmail] = useState(false);
    const [copied, setCopied] = useState(false);

    if (result.totalCount === 0) return null;

    // Group items by category, preserving order of first appearance
    const categories: EvidenceCategory[] = [];
    const grouped = new Map<EvidenceCategory, EvidenceRequest[]>();
    for (const item of result.items) {
        if (!grouped.has(item.category)) {
            categories.push(item.category);
            grouped.set(item.category, []);
        }
        grouped.get(item.category)!.push(item);
    }

    const toggleCat = (cat: EvidenceCategory) => {
        setExpandedCats(prev => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat);
            else next.add(cat);
            return next;
        });
    };

    const expandAll = () => setExpandedCats(new Set(categories));
    const collapseAll = () => setExpandedCats(new Set());

    const caseName = [profile.deceased?.firstName, profile.deceased?.lastName]
        .filter(Boolean).join(' ') || undefined;
    const emailText = useMemo(
        () => generateEvidenceEmailText(result, caseName),
        [result, caseName]
    );

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(emailText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const highCount = result.items.filter(i => i.priority === 'high').length;

    const subtitle = `${result.totalCount} document${result.totalCount !== 1 ? 's' : ''} identified` +
        (highCount > 0 ? ` · ${highCount} high priority` : '');

    return (
        <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-6">
                <SectionHeader
                    numeral=""
                    title="Documents still required"
                    subtitle={subtitle}
                />
            </div>

            {/* Controls */}
            <div className="px-6 py-3 border-b border-[#E8E4DB] flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <button
                        onClick={expandAll}
                        className="text-[11px] font-medium text-[#3730A3] hover:text-[#2E2780] hover:underline"
                    >
                        Expand all
                    </button>
                    <span className="text-[#AFA99F]">|</span>
                    <button
                        onClick={collapseAll}
                        className="text-[11px] font-medium text-[#3730A3] hover:text-[#2E2780] hover:underline"
                    >
                        Collapse all
                    </button>
                </div>
                <button
                    onClick={() => setShowEmail(!showEmail)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-[#3F3D39] bg-transparent border border-[#E8E4DB] rounded hover:bg-[#F3EFE6] transition-colors"
                >
                    <Mail className="w-3.5 h-3.5" />
                    Generate email request
                </button>
            </div>

            {/* Email panel */}
            {showEmail && (
                <div className="px-6 py-4 bg-[#FAFAF8] border-b border-[#E8E4DB]">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762]">Email template</h4>
                        <button
                            onClick={handleCopyEmail}
                            className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-[#3F3D39] bg-white border border-[#E8E4DB] rounded hover:bg-[#F3EFE6] transition-colors"
                        >
                            {copied
                                ? <><Check className="w-3 h-3 text-[#3730A3]" /> Copied</>
                                : <><Copy className="w-3 h-3" /> Copy to clipboard</>}
                        </button>
                    </div>
                    <pre className="text-[12px] text-[#1A1A1A] bg-white border border-[#E8E4DB] rounded p-4 whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto">
                        {emailText}
                    </pre>
                </div>
            )}

            {/* Category accordion */}
            <div className="divide-y divide-[#E8E4DB]">
                {categories.map(cat => {
                    const items = grouped.get(cat)!;
                    const meta = CATEGORY_META[cat];
                    const isExpanded = expandedCats.has(cat);
                    const catHighCount = items.filter(i => i.priority === 'high').length;

                    return (
                        <div key={cat}>
                            <button
                                onClick={() => toggleCat(cat)}
                                className="w-full flex items-center justify-between px-6 py-3 hover:bg-[#F3EFE6] transition-colors text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-[#6B6762]">{meta.icon}</span>
                                    <span className="text-[13px] font-medium text-[#1A1A1A]">{meta.label}</span>
                                    <span className="text-[11px] text-[#6B6762] font-mono tabular-nums">
                                        {items.length} item{items.length !== 1 ? 's' : ''}
                                    </span>
                                    {catHighCount > 0 && (
                                        <span className="text-[10px] tracking-[0.18em] uppercase text-[#3730A3] font-mono tabular-nums">
                                            {catHighCount} high
                                        </span>
                                    )}
                                </div>
                                {isExpanded
                                    ? <ChevronUp className="w-4 h-4 text-[#6B6762]" />
                                    : <ChevronDown className="w-4 h-4 text-[#6B6762]" />}
                            </button>

                            {isExpanded && (
                                <div className="px-6 pb-4 space-y-2 animate-in slide-in-from-top-1">
                                    {/* Sub-group by group label */}
                                    {(() => {
                                        const groups = [...new Set(items.map(i => i.group))];
                                        return groups.map(group => {
                                            const groupItems = items.filter(i => i.group === group);
                                            return (
                                                <div key={group} className="pl-7">
                                                    {groups.length > 1 && (
                                                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1.5">{group}</p>
                                                    )}
                                                    {groupItems.map(item => (
                                                        <div key={item.id} className="flex items-start justify-between gap-3 py-2 border-b border-dotted border-[#E8E4DB] last:border-0">
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-[13px] text-[#1A1A1A] font-medium">{item.document}</p>
                                                                <p className="text-[12px] text-[#3F3D39] mt-0.5 leading-relaxed">{item.reason}</p>
                                                                {item.contactHint && (
                                                                    <p className="text-[11px] text-[#AFA99F] mt-0.5 italic">{item.contactHint}</p>
                                                                )}
                                                            </div>
                                                            <span className="shrink-0 text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mt-0.5">
                                                                {item.priority}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
